import {Injectable, LOCALE_ID, inject} from '@angular/core';
import {Analytics, logEvent} from '@angular/fire/analytics';
import {Database, TransactionResult, get, objectVal, push, ref, remove, runTransaction, serverTimestamp, set, update} from '@angular/fire/database';
import {Router} from '@angular/router';
import {EMPTY, Observable, catchError, combineLatest, defer, forkJoin, from, interval, map, of, shareReplay, startWith, switchMap, take, throwError, timer} from 'rxjs';
import {AllTeamData, Announcement, EventSummary, FtaNotes, FullSetting, FullTask, InspectionSummary, Lineups, Match, MatchAdvancement, MatchTeam, MatchWithKey, MultiAnnouncement, PartsRequest, Rankings, Task, TaskDefinition, TeamNotification, TeamNotificationSummary} from './models';
import {addDynamicQueueTime, addMatchLabels, getAnnouncementTitleText, getLocalTimeString, getMatchTypeFromMatchKey, getMondayBeforeTimestamp, getSortedMatches, matchKeyToText} from './utils';

import {EventSummariesService} from './event-summaries.service';
import {PitMap, PitMapLibrary} from './map.models';
import {FmsField, PracticeFields, PracticeMatch, QueuedTeam, TimedField} from './practice.models';
import {addEstimatedWaits, practiceQueueComparator} from './practice.utils';
import {RpcService} from './rpc.service';
import {ToastService} from './toast';

const FIELD_TIMER_DWELL_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class EventDataService {
  private readonly database = inject(Database);
  private readonly rpcService = inject(RpcService);
  private readonly locale = inject(LOCALE_ID);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly analytics = inject(Analytics, {optional: true});
  private readonly eventSummariesService = inject(EventSummariesService);

  // Shared timer to synchronize regular updates.
  private readonly oneSecondTimer$ = timer(0, 1000);

  private getServerOffset() {
    return objectVal<number>(ref(this.database, `.info/serverTimeOffset`)).pipe(shareReplay(1));
  }

  now() {
    return this.getServerOffset().pipe(take(1), map(offset => Date.now() + offset));
  }

  nowTimer(interval = 1000) {
    const timer$ = interval === 1000 ? this.oneSecondTimer$.pipe(startWith(0)) : timer(0, interval);
    return combineLatest([this.getServerOffset(), timer$]).pipe(map(([offset]) => Date.now() + offset));
  }

  private getFieldTimer(eventKey: string) {
    return objectVal<number|null>(ref(this.database, `/events/${eventKey}/fieldTimer`));
  }

  getFieldTimerActive(eventKey: string) {
    return combineLatest([this.nowTimer(), this.getFieldTimer(eventKey)]).pipe(map(([now, timer]) => {
      return timer && (timer + FIELD_TIMER_DWELL_MS) > now ? timer : null;
    }));
  }

  getEventSummaries(onlyLive = false, sortBy: 'name' | 'start' = 'name'): Observable<EventSummary[]> {
    return combineLatest([
      this.eventSummariesService.get(),
      this.nowTimer(1000 * 60 * 60).pipe(startWith(Date.now())),
    ]).pipe(
      map(([obj, now]) => {
        const results: EventSummary[] = [];

        for (const [key, s] of Object.entries(obj || {})) {
          if (s.end < now) continue;
          if(onlyLive) {
            if (!s.exactDates && getMondayBeforeTimestamp(s.start) > now) continue;
            if (s.exactDates && s.start > now) continue;
          }

          results.push({ ...s, key });
        }

        if (sortBy === 'name') {
          results.sort((a, b) => a.name.localeCompare(b.name));
        }
        if (sortBy === 'start') {
          results.sort((a, b) => a.start - b.start);
        }

        return results;
      })
    );
  }

  getMatchObject(eventKey: string): Observable<{ [matchKey: string]: Match }> {
    return objectVal(ref(this.database, `/events/${eventKey}/matches`));
  }

  getMatchList(eventKey: string): Observable<MatchWithKey[]> {
    return combineLatest([
      this.getMatchObject(eventKey).pipe(map((matchesObj) => getSortedMatches(matchesObj))),
      this.getSettings(eventKey),
      this.nowTimer(1000 * 10),
      this.getFieldTimer(eventKey),
    ]).pipe(
      map(([matches, settings, now, fieldTimer]) => {
        const newMatches = matches.concat();
        addDynamicQueueTime(
          newMatches,
          settings?.practiceCycleTimeSeconds,
          settings?.qualificationCycleTimeSeconds,
          settings?.playoffCycleTimeSeconds,
          settings?.queueSingleMatch,
          settings?.queueExtraMatch,
          now,
          fieldTimer,
          settings?.autoQueueEnabled,
        );
        addMatchLabels(newMatches, eventKey);

        return newMatches;
      })
    );
  }

  getSettings(eventKey: string): Observable<any> {
    return objectVal(ref(this.database, `/events/${eventKey}/settings`));
  }

  getSetting(eventKey: string, setting: string): Observable<any> {
    return objectVal(ref(this.database, `/events/${eventKey}/settings/${setting}`));
  }

  getAllianceNames(eventKey: string, short?: boolean): Observable<string[]> {
    return combineLatest([
      this.getSetting(eventKey, 'allianceNames'),
      this.getSetting(eventKey, 'playoffAllianceCount'),
    ]).pipe(
      map(([nameOverrides, count]) => {
        const result = [];
        for (let i = 0; i < count; i++) {
          if (nameOverrides?.[i]) {
            result.push(nameOverrides[i]);
          } else {
            result.push(short ? `${i + 1}` : $localize`Alliance ${i + 1}`);
          }
        }
        return result;
      }),
      startWith(
        short
          ? ['1', '2', '3', '4', '5', '6', '7', '8']
          : [
              'Alliance 1',
              'Alliance 2',
              'Alliance 3',
              'Alliance 4',
              'Alliance 5',
              'Alliance 6',
              'Alliance 7',
              'Alliance 8',
            ]
      )
    );
  }

  hasEventEnded(eventKey: string): Observable<boolean> {
    return this.getSetting(eventKey, 'ended').pipe(map((e) => !!e));
  }

  getEventName(eventKey: string, full = false): Observable<string | undefined> {
    const seed = this.eventSummariesService.getCache();
    const seedName = seed?.[eventKey]?.name;
    let result = this.getSetting(eventKey, 'name').pipe(map((name) => name || undefined));
    if(seedName) {
      result = result.pipe(startWith(seedName));
    }

    result = result.pipe(map(name => full ? name : name?.replaceAll(/.*Championship\s*-\s*/g, '')?.replaceAll(/.*Event\s*-\s*/g, '')));

    return result;
  }

  getEventNameForSentence(eventKey: string): Observable<string | undefined> {
    return combineLatest([this.getEventName(eventKey), this.getSetting(eventKey, 'namePrefix')]).pipe(
      map(([name, prefix]) => (name ? (prefix && this.locale === 'en' ? `the ${name}` : name) : undefined))
    );
  }

  getMultiEventNames(eventKeys: string[]): Observable<{[eventKey: string]: string|undefined}> {
    const obj: {[key: string]: Observable<string|undefined>} = {};
    for(const k of eventKeys) {
      obj[k] = this.getEventName(k).pipe(map(n => n?.replaceAll(' Division', '')));
    }
    return combineLatest(obj);
  }

  getCsaSlack(eventKey: string): Observable<{ url: string; channel: string }> {
    return objectVal<{ url: string; channel: string }>(ref(this.database, `events/${eventKey}/csaSlack`)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getFullSettings(eventKey: string): Observable<{ [key: string]: FullSetting }> {
    const defs$ = objectVal<{ [key: string]: FullSetting }>(ref(this.database, `settings`));
    return combineLatest([this.getSettings(eventKey), defs$]).pipe(
      map(([values, defs]) => {
        for (const [key, def] of Object.entries(defs)) {
          if (def.type === 'datetime-local') {
            def.value = getLocalTimeString(values[key]);
          } else {
            def.value = values[key];
          }
        }

        return defs;
      })
    );
  }

  getAllLineups(eventKey: string): Observable<Lineups | null> {
    return objectVal<Lineups>(ref(this.database, `events/${eventKey}/lineups`)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getLineup(eventKey: string, matchKey: string, allianceColor: 'red' | 'blue'): Observable<string[] | undefined> {
    return objectVal<{ [station: string]: string }>(
      ref(this.database, `events/${eventKey}/lineups/${matchKey}/${allianceColor}`)
    ).pipe(
      map((obj) => {
        if (!obj) return undefined;

        return Object.keys(obj).map(function (k) {
          return obj[k];
        });
      }),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  setLineup(eventKey: string, matchKey: string, allianceColor: 'red' | 'blue', lineup: string[]): Observable<any> {
    return from(set(ref(this.database, `events/${eventKey}/lineups/${matchKey}/${allianceColor}`), lineup)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  updateLineup(
    eventKey: string,
    matchKey: string,
    allianceColor: 'red' | 'blue',
    lineup: MatchTeam[]
  ): Observable<any> {
    return from(
      update(ref(this.database, `events/${eventKey}/matches/${matchKey}`), {
        [allianceColor]: lineup,
        [`${allianceColor}LineupSet`]: true,
      })
    ).pipe(catchError((e) => this.handleDbError(e, eventKey)));
  }

  updateSetting(eventKey: string, settingKey: string, value: any): Observable<any> {
    return from(set(ref(this.database, `events/${eventKey}/settings/${settingKey}`), value)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getTasks(eventKey: string): Observable<FullTask[]> {
    const defs$: Observable<{ [taskId: string]: TaskDefinition }> = objectVal(ref(this.database, `/tasks`));
    const tasks$: Observable<{ [taskKey: string]: Task }> = objectVal(ref(this.database, `/events/${eventKey}/tasks`));
    return combineLatest([defs$, tasks$]).pipe(
      map(([defs, tasks]) => {
        const result: FullTask[] = [];
        for (const [taskKey, task] of Object.entries(tasks || {})) {
          const def = defs[task.id];

          if (!def) {
            console.warn('No task def found', task.id);
            continue;
          }

          let title = def.title;
          if (task.match) {
            title = title.replaceAll('{MATCH}', matchKeyToText(task.match, eventKey));
          }

          result.push({ ...task, ...def, key: taskKey, title });
        }

        result.sort((a, b) => b.created - a.created);

        return result;
      }),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getTeamData(eventKey: string): Observable<AllTeamData> {
    return combineLatest([
      objectVal<AllTeamData>(ref(this.database, `events/${eventKey}/teams`)).pipe(map((obj) => obj || {})),
      this.getMatchList(eventKey),
    ]).pipe(map(([data, matches]) => {
      for(const [team, d] of Object.entries(data)) {
        const match = matches.find(m => this.teamPlayedInMatch(team, m));
        if(match) {
          d.hasPlayed = match.key;
        } else {
          d.hasPlayed = undefined;
        }
      }
      return data;
    }));
  }

  getTeamInspectionSummary(eventKey: string, team: string): Observable<InspectionSummary|null> {
    return combineLatest([this.getTeamData(eventKey), objectVal<InspectionSummary|null>(ref(this.database, `events/${eventKey}/inspectionSummaries/${team}`))]).pipe(
      map(([teamData, inspectionSummary]) => {
        if(inspectionSummary) {
          return inspectionSummary;
        }

        // Fallback behavior for demo events.
        if(teamData[team]?.inspected) {
          return {status: 'complete', inspected: true} as InspectionSummary;
        }

        return null;
      }),
    );
  }

  getAllTeamInspectionSummary(eventKey: string): Observable<{[team: string]: InspectionSummary}|null> {
    return combineLatest([this.getTeamData(eventKey), objectVal<{[team: string]: InspectionSummary}|null>(ref(this.database, `events/${eventKey}/inspectionSummaries`))]).pipe(
      map(([teamData, inspectionSummaries]) => {
        if(inspectionSummaries) {
          return inspectionSummaries;
        }

        // Fallback behavior for demo events.
        if(teamData) {
          const result: {[team: string]: InspectionSummary} = {};
          let count = 0;
          for(const [team, td] of Object.entries(teamData)) {
            if(td.inspected) {
              result[team] = {status: 'complete', inspected: true};
              count++
            }
          }

          if(count) {
            return result;
          }
        }

        return null;
      }),
    );
  }

  private teamPlayedInMatch(team: string, match: MatchWithKey): boolean {
    if(match.status !== 'complete') return false;

    const matchTeam = match.red?.find(t => t.team === team) || match.blue?.find(t => t.team === team);
    return matchTeam?.status === 'checked_in';
  }

  getTeams(eventKey: string): Observable<string[]> {
    return this.getTeamData(eventKey).pipe(map((obj) => Object.keys(obj || {})));
  }

  updatePit(eventKey: string, team: string, pit: string): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/teams/${team}/pit`), pit)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  updatePitMap(eventKey: string, map: PitMap|null): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/pitMap`), map)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  hasPitMap(eventKey: string): Observable<boolean> {
    return defer(() => get(ref(this.database, `events/${eventKey}/pitMap`))).pipe(map(snapshot => snapshot.exists()));
  }

  getPitMap(eventKey: string): Observable<PitMap | null> {
    return objectVal<PitMap>(ref(this.database, `events/${eventKey}/pitMap`));
  }

  getPitMapLibrary(): Observable<PitMapLibrary | null> {
    return objectVal<PitMapLibrary>(ref(this.database, `pitMaps/library`));
  }

  getPitMapFromLibrary(eventKey: string): Observable<PitMap | null> {
    return objectVal<PitMap>(ref(this.database, `pitMaps/maps/${eventKey}`)).pipe(switchMap(libraryMap => libraryMap ? of(libraryMap) : this.getPitMap(eventKey)));
  }

  updatePickAPitWindowOpens(eventKey: string, team: string, windowOpens: number|null): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/teams/${team}/pickAPitWindowOpens`), windowOpens)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  updateAllianceSelectionCheckIn(eventKey: string, team: string, checkedIn: boolean): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/teams/${team}/allianceSelectionCheckIn`), checkedIn)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getAlliances(eventKey: string): Observable<string[][] | null> {
    return objectVal<{ [i: string]: { [i: string]: string } }>(ref(this.database, `events/${eventKey}/alliances`)).pipe(
      map((db) => {
        const result: string[][] = [];
        for(const [i, a] of Object.entries(db || {})) {
          if(!result[+i]) {
            result[+i] = [];
          }
          for(const [j, t] of Object.entries(a)) {
            result[+i][+j] = t;
          }
        }
        return result?.length ? result : null;
      })
    );
  }

  updateQueueTime(eventKey: string, match: string, queueTime: number | null) {
    return set(ref(this.database, `events/${eventKey}/matches/${match}/queueTime`), queueTime);
  }

  updateBreakAfter(eventKey: string, match: string, breakAfter: string) {
    let newBreakAfter: string | null = breakAfter;
    if (!newBreakAfter) {
      newBreakAfter = null;
    }
    return set(ref(this.database, `events/${eventKey}/matches/${match}/breakAfter`), breakAfter);
  }

  moveMatch(eventKey: string, match: string, afterMatchId: string|null): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/matches/${match}/afterMatchId`), afterMatchId));
  }

  getTeamNotifications(eventKey: string): Observable<TeamNotificationSummary[]> {
    return objectVal(ref(this.database, `events/${eventKey}/teamNotifications`)).pipe(
      map((db: any) =>
        Object.entries(db || {})
          .filter(([team, keyedData]) => !(keyedData as any).zzzhidden)
          .map(([team, keyedData]) => {
            const key = Object.keys(keyedData as any)[0];
            const data = (keyedData as any)[Object.keys(keyedData as any)[0]];
            return {
              team,
              key,
              phoneNumberCount: Object.keys(data.phoneNumbers || {}).length,
              slack: !!data.slack,
              discord: !!data.discord,
              announcementsSlack: !!data.announcements?.slack,
              announcementsDiscord: !!data.announcements?.discord,
            };
          })
      ),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getTeamNotificationData(eventKey: string, teamNumber: string, key: string): Observable<TeamNotification> {
    return objectVal<TeamNotification>(
      ref(this.database, `events/${eventKey}/teamNotifications/${teamNumber}/${key}`)
    ).pipe(catchError((e) => this.handleDbError(e, eventKey)));
  }

  getUsers(eventKey: string): Observable<{ [uid: string]: string[] }> {
    return objectVal<{ [uid: string]: string[] }>(ref(this.database, `events/${eventKey}/authz`)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getUsersWithEmails(
    eventKey: string
  ): Observable<{ uid: string; email?: string; deviceName?: string; permissions: string[] }[]> {
    return this.getUsers(eventKey).pipe(
      switchMap((dbUsers) =>
        this.rpcService
          .getUserDisplayInfo(eventKey, Object.keys(dbUsers || {}))
          .pipe(
            map((displayInfo) =>
              Object.keys(dbUsers || {}).map((uid) => ({
                uid,
                permissions: Object.keys(dbUsers[uid] || {}),
                email: displayInfo[uid],
              }))
            )
          )
      ),
      switchMap((users) => {
        if (!users.length) {
          return of([]);
        }
        return forkJoin(
          users.map((user) =>
            user.email
              ? of(user)
              : this.getDeviceName(user.uid).pipe(
                  take(1),
                  map((name) => ({ ...user, deviceName: name }))
                )
          )
        );
      }),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getRequests(eventKey: string): Observable<{ uid: string; email?: string; deviceName?: string; role: string }[]> {
    return objectVal<{ [uid: string]: { email?: string; role: string; deviceName?: string } }>(
      ref(this.database, `events/${eventKey}/requests`)
    ).pipe(
      map((requestObj) => {
        const result = [];
        for (const [uid, request] of Object.entries(requestObj || {})) {
          result.push({ uid, email: request.email, deviceName: request.deviceName, role: request.role });
        }

        return result;
      }),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getInvites(eventKey: string): Observable<{ key: string; email?: string; role: string, created?: number }[]> {
    return objectVal<{ [key: string]: { email?: string; role: string, created?: number } }>(
      ref(this.database, `events/${eventKey}/invites`)
    ).pipe(
      map((inviteObj) => {
        const result = [];
        for (const [key, invite] of Object.entries(inviteObj || {})) {
          result.push({ key, email: invite.email, role: invite.role, created: invite.created });
        }

        return result;
      }),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getInvite(eventKey: string, key: string): Observable<{ email?: string; role: string; created?: number } | null> {
    return objectVal<{ email?: string; role: string; created?: number }>(
      ref(this.database, `events/${eventKey}/invites/${key}`)
    );
  }

  grantPermissions(eventKey: string, uid: string, permissions: string[]) {
    const newPermissions: { [permission: string]: boolean } = {};
    for (const permission of permissions) {
      newPermissions[permission] = true;
    }
    return update(ref(this.database, `events/${eventKey}/authz/${uid}`), newPermissions);
  }

  deleteRequest(eventKey: string, uid: string): Observable<void> {
    return from(remove(ref(this.database, `events/${eventKey}/requests/${uid}`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  removeInvite(eventKey: string, key: string): Observable<void> {
    return from(remove(ref(this.database, `events/${eventKey}/invites/${key}`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  createTimedInvite(eventKey: string, role: string): Observable<string> {
    let key: string;

    try {
      key = self.crypto.randomUUID();
    } catch {
      return throwError(() => new Error('Unable to generate invite UUID'));
    }

    return from(set(ref(this.database, `events/${eventKey}/invites/${key}`), {role, created: serverTimestamp(), type: 'timed'})).pipe(
      catchError((e) => this.handleDbError(e, eventKey)),
      map(() => key),
    );
  }

  deleteUser(eventKey: string, uid: string): Observable<void> {
    return from(remove(ref(this.database, `events/${eventKey}/authz/${uid}`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getRoleDefinitions(includeHidden = false) {
    return objectVal<{ [key: string]: { title: string; description: string; permissions: string[]; sort: number, hidden?: boolean } }>(
      ref(this.database, `permissions/roles`)
    ).pipe(map(roles => {
      const result: { [key: string]: { title: string; description: string; permissions: string[]; sort: number, hidden?: boolean }} = {};

      for(const [key, role] of Object.entries(roles || {})) {
        if(role.hidden && !includeHidden) continue;

        result[key] = role;
      }

      return result;
    }));
  }

  getPermissionDefinitions() {
    return objectVal<{ [key: string]: { title: string; } }>(
      ref(this.database, `permissions/permissions`)
    );
  }

  saveDeviceName(uid: string, name: string): Observable<void> {
    return from(set(ref(this.database, `deviceNames/${uid}`), name));
  }

  getDeviceName(uid: string): Observable<string> {
    return objectVal<string>(ref(this.database, `deviceNames/${uid}`));
  }

  deleteMatch(eventKey: string, matchKey: string): Promise<void> {
    return remove(ref(this.database, `events/${eventKey}/matches/${matchKey}`));
  }

  addFillerMatch(eventKey: string): Observable<void> {
    return this.getSetting(eventKey, 'matchAllianceSize').pipe(
      take(1),
      switchMap(
        allianceSize => runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches) => {
        let maxPracticeMatchNumber = 0;

        if (matches) {
            for (const matchKey in matches) {
                if (matchKey.substring(0, 1) === 'p') {
                    const matchNum = +matchKey.substring(1);
                    if (matchNum > maxPracticeMatchNumber) {
                        maxPracticeMatchNumber = matchNum;
                    }
                }
            }
        } else {
          matches = {};
        }

        matches[`p${maxPracticeMatchNumber + 1}`] ={
            status: 'waiting',
            filler: true,
            red: Array(allianceSize).fill({team: '', status:'waiting'}),
            blue: Array(allianceSize).fill({team: '', status:'waiting'}),
        };

        return matches;
      })
    ), map(()=>{}));
  }

  setQueueBeforeOpeningCeremonies(
    eventKey: string,
    matchKey: string,
    queueBeforeOpeningCeremonies: boolean
  ): Promise<void> {
    return set(
      ref(this.database, `events/${eventKey}/matches/${matchKey}/queueBeforeOpeningCeremonies`),
      queueBeforeOpeningCeremonies
    );
  }

  getRankings(eventKey: string): Observable<Rankings | undefined> {
    return objectVal<Rankings>(ref(this.database, `events/${eventKey}/rankings`));
  }

  addAnnouncement(
    eventKeys: string[],
    message: string,
    title: string,
    type: 'current' | 'past' = 'current',
  ): Observable<any> {
    const id = push(ref(this.database)).key;
    const dbUpdate: {[path: string]: object} = {};
    for(const k of eventKeys) {
      dbUpdate[`${k}/announcements/${type}/${id}`] = {
        message,
        title,
        timestamp: serverTimestamp(),
      };
    }
    return from(
      update(ref(this.database, `events`), dbUpdate)
    ).pipe(catchError((e) => this.handleDbError(e, eventKeys[0])));
  }

  removeAnnouncement(eventKey: string, key: string, type: 'current' | 'past' = 'current'): Observable<any> {
    return from(remove(ref(this.database, `events/${eventKey}/announcements/${type}/${key}`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  moveAnnouncement(
    eventKeys: string[],
    announcement: Announcement,
    moveFrom: 'current' | 'past' = 'current'
  ): Observable<any> {
    const moveTo = moveFrom === 'current' ? 'past' : 'current';
    const dbUpdate: {[path: string]: object|null} = {};
    for(const k of eventKeys) {
      dbUpdate[`${k}/announcements/${moveFrom}/${announcement.key}`] = null;
      dbUpdate[`${k}/announcements/${moveTo}/${announcement.key}`] = announcement;
    }
    return from(update(ref(this.database, `events`), dbUpdate)).pipe(
      catchError((e) => this.handleDbError(e, eventKeys[0]))
    );
  }

  getAnnouncements(eventKey: string, type: 'current' | 'past' = 'current'): Observable<Announcement[]> {
    return objectVal<{ [key: string]: Announcement }>(
      ref(this.database, `events/${eventKey}/announcements/${type}`)
    ).pipe(
      map((obj) => {
        const result: Announcement[] = [];
        for (const [key, a] of Object.entries(obj || {})) {
          result.unshift({ ...a, key, localizedTitle: getAnnouncementTitleText(a.title) });
        }

        return result;
      })
    );
  }

  getMultiAnnouncements(eventKeys: string[], type: 'current'|'past'='current'): Observable<MultiAnnouncement[]> {
    const requests: {[eventKey: string]: Observable<{ [key: string]: Announcement }>} = {};
    for(const eventKey of Object.values(eventKeys)) {
      requests[eventKey] = objectVal<{ [key: string]: Announcement }>(ref(this.database, `events/${eventKey}/announcements/${type}`));
    }
    return combineLatest(requests).pipe(map(all => {
      const merged: { [key: string]: MultiAnnouncement } = {};
      for(const [eventKey, announcements] of Object.entries(all)) {
        for(const [key, a] of Object.entries(announcements || {})) {
          if(!merged[key]) {
            merged[key] = {...a, eventKeys: []};
          }
          merged[key].eventKeys.push(eventKey);
        }
      }
      
      const result: MultiAnnouncement[] = [];
      for (const [key, a] of Object.entries(merged || {})) {
        result.unshift({ ...a, key, localizedTitle: getAnnouncementTitleText(a.title) });
      }

      return result;
    }));
  }

  importMatches(eventKey: string, matches: { [key: string]: Match }): Observable<void> {
    return from(update(ref(this.database, `events/${eventKey}/matches`), matches)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getPartsRequests(
    eventKey: string,
    team?: string,
    status?: 'pending' | 'active' | 'denied'
  ): Observable<PartsRequest[]> {
    return objectVal<{ [key: string]: PartsRequest }>(ref(this.database, `events/${eventKey}/partsRequests`)).pipe(
      map((obj) => {
        const result: PartsRequest[] = [];
        for (const [key, a] of Object.entries(obj || {})) {
          if (team && a.team !== team) continue;
          if (status && a.status !== status) continue;

          result.unshift({ ...a, key });
        }

        return result;
      })
    );
  }

  updatePartsRequestStatus(eventKey: string, key: string, status: 'active' | 'denied'): Observable<void> {
    return from(set(ref(this.database, `events/${eventKey}/partsRequests/${key}/status`), status)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  removePartsRequest(eventKey: string, key: string): Observable<void> {
    return from(remove(ref(this.database, `events/${eventKey}/partsRequests/${key}`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  addPartsRequest(eventKey: string, team: string, parts: string): Observable<any> {
    return from(
      push(ref(this.database, `events/${eventKey}/partsRequests`), {
        team,
        parts,
        status: 'active',
        timestamp: serverTimestamp(),
      })
    ).pipe(catchError((e) => this.handleDbError(e, eventKey)));
  }

  getDemoEventKey(uid: string): Observable<string | undefined> {
    return objectVal<string>(ref(this.database, `demo/${uid}/eventKey`));
  }

  getFtaNotes(eventKey: string): Observable<FtaNotes> {
    return objectVal<FtaNotes>(ref(this.database, `events/${eventKey}/ftaNotes`)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getFtaNoteRequests(eventKey: string): Observable<{[team: string]: {[match: string]: {requestCsa?: boolean; requestLri?: boolean}}}> {
    return objectVal<{[team: string]: {[match: string]: {requestCsa?: boolean; requestLri?: boolean}}}>(ref(this.database, `events/${eventKey}/ftaNoteRequests`)).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  isCsaSlackConfigured(eventKey: string): Observable<boolean> {
    return objectVal<string>(ref(this.database, `events/${eventKey}/csaSlack/channel`)).pipe(map((c) => !!c));
  }

  deleteAllLineups(eventKey: string): Observable<void> {
    return from(remove(ref(this.database, `events/${eventKey}/lineups`))).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  deleteAllMatches(eventKey: string, matchType: string): Observable<any> {
    const matchTypes = [matchType];
    if (matchType === 'de') {
      matchTypes.push('f');
    }

    return from(
      runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches) => {
        if (!matches) return matches;

        const filteredMatches: { [matchKey: string]: Match } = {};
        for (let [matchKey, m] of Object.entries(matches)) {
          if (!matchTypes.includes(getMatchTypeFromMatchKey(matchKey))) {
            filteredMatches[matchKey] = m as Match;
          }
        }

        return filteredMatches;
      })
    ).pipe(
      switchMap(() => matchType === 'de' ? remove(ref(this.database, `events/${eventKey}/lineups`)) : of(undefined)),
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  getOffseasonRegistrations(uid: string): Observable<{ [eventKey: string]: string }> {
    return objectVal(ref(this.database, `applications/${uid}/events`));
  }

  deleteOffseasonRegistration(uid: string, eventKey: string): Observable<void> {
    return from(remove(ref(this.database, `applications/${uid}/events/${eventKey}`))).pipe(
      catchError((e) => this.handleDbError(e))
    );
  }

  getPitPicks(eventKey: string): Observable<{ [pit: string]: string }> {
    return objectVal<{ [pit: string]: string }>(ref(this.database, `events/${eventKey}/pickAPit`)).pipe(
      map((picks) => picks || {})
    );
  }

  getAllianceSelectionTimer(eventKey: string): Observable<{skew: number, started?: number, duration?: number, defaultDuration?: number, display?: string, remainingDuration?: number}|undefined> {
    return combineLatest([
      objectVal<{started?: number, duration?: number}|undefined>(ref(this.database, `/events/${eventKey}/allianceSelectionTimer`)),
      this.getServerOffset(),
      this.getSetting(eventKey, 'allianceSelectionTimerDuration'),
    ]).pipe(
      switchMap(t => t[2] ? interval(100).pipe(map(() => t)) : of([])),
      map(([timer, skew, defaultDuration]) => {
        if(!defaultDuration) return undefined;

        let started = timer?.started;
        let display: string|undefined = undefined;
        const duration = timer?.duration || defaultDuration;
        let remainingDuration: number|undefined = undefined;
        if(started) {
          const target = started + duration;
          remainingDuration = target - (Date.now() + skew);
        }

        const diffToDisplay = remainingDuration || duration;
        if(diffToDisplay <= 0) {
          display = `00:00`;
        } else {
          const diffSeconds = Math.ceil(diffToDisplay / 1000);
          display = `${String(Math.floor(diffSeconds / 60)).padStart(2, '0')}:${String(diffSeconds % 60).padStart(2, '0')}`;
        }

        return {skew, started, duration, defaultDuration, display, remainingDuration};
      }),
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  startAllianceSelectionTimer(eventKey: string, duration: number, skew: number) {
    return from(set(ref(this.database, `events/${eventKey}/allianceSelectionTimer`), {started: Date.now() + skew, duration})).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  pauseAllianceSelectionTimer(eventKey: string, duration: number) {
    return from(set(ref(this.database, `events/${eventKey}/allianceSelectionTimer`), {started: null, duration})).pipe(
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  requestAccess(eventKey: string, role: string, uid: string, email: string): Observable<void> {
    const deviceName$ = email ? of('') : this.getDeviceName(uid);
    return deviceName$.pipe(take(1), switchMap(deviceName => from(set(ref(this.database, `events/${eventKey}/requests/${uid}`), {email, role, deviceName}))));
  }

  updateAllianceTeam(eventKey: string, allianceNumber: number, teamIndex: number, team: string) {
    return this.getSetting(eventKey, 'matchAllianceSize').pipe(
      switchMap(allianceSize => {
        return runTransaction(ref(this.database, `events/${eventKey}/alliances/${allianceNumber}`), dbAlliance => {
          let alliance = dbAlliance || Array(allianceSize).fill('');

          alliance[teamIndex] = team;

          if(alliance[allianceSize] === '') {
            alliance.pop();
          }

          return alliance;
        });
      }),
      switchMap(result => {
        const alliance: string[] = result.snapshot.val();
        if(!alliance || !result.committed) {
          throw new Error('Failed to commit alliance update');
        }

        let updatedMatches: {matchKey: string, color: 'red'|'blue'}[] = [];
        return from(runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches: {[matchKey: string]: Match}) => {
          if(!matches) return {};
          updatedMatches = [];

          for(const [matchKey, match] of Object.entries(matches)) {
            if ((match.blueAlliance === allianceNumber || match.redAlliance === allianceNumber) && !match.winner) {
              const color = match.blueAlliance === allianceNumber ? 'blue' : 'red';
              match[color] = alliance.map(y => ({ team: y, status: 'waiting' }));
              match[`${color}LineupSet`] = false;

              updatedMatches.push({matchKey, color});
            }
          }

          return matches;
        })).pipe(map(result => result.committed ? updatedMatches : undefined));
      }),
      switchMap(updatedMatches => {
        if(updatedMatches?.length) {
          return runTransaction(ref(this.database, `events/${eventKey}/lineups`), lineups => {
            if (!lineups) return {};
            for(const u of updatedMatches) {
              delete lineups[u.matchKey]?.[u.color];
            }

            return lineups;
          });
        }

        return of(undefined);
      }),
    );
  }

  generatePlayoffMatches(eventKey: string) {
    return this.getMatchObject(eventKey).pipe(
      take(1),
      switchMap(existingMatches => {
        if(Object.keys(existingMatches || {}).find(k => k.startsWith('de'))) {
          const e: any = new Error('Playoff matches already exist, not overriding');
          e.error = {snackbarMessage: 'Playoff matches already exist, not overriding'};
          throw e;
        }

        return this.getSetting(eventKey, 'playoffType');
      }),
      take(1),
      switchMap(playoffType => {
        if(playoffType === 'custom') {
          const e: any = new Error('Import custom playoffs from the match import  page');
          e.error = {snackbarMessage: 'Import custom playoffs from the match import  page'};
          throw e;
        }
        return combineLatest([objectVal<{[matchKey: string]: Match}>(ref(this.database, `playoffs/${playoffType}`)), this.getSetting(eventKey, 'fieldCount')]);
      }),
      take(1),
      switchMap(([playoffMatches, fieldCount]) =>  {
        if(!playoffMatches) {
          const e: any = new Error('Unable to generate playoff matches');
          e.error = {snackbarMessage: 'Unable to generate playoff matches'};
          throw e;
        }

        if(fieldCount && fieldCount > 1) {
          let i = 0;
          for(const m of Object.values(playoffMatches)) {
            const n = (i % fieldCount) + 1;
            m.location = `Field ${n}`;
            m.locationFieldNumber = n;

            i++;
          }
        }

        return this.importMatches(eventKey, playoffMatches);
      }),
      take(1),
      switchMap(() => this.getAlliances(eventKey)),
      take(1),
      switchMap(alliances =>  from(runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches: {[matchKey: string]: Match}) => {
          if(!matches) return {};
          if(!alliances) return;

          for(const [matchKey, match] of Object.entries(matches)) {
            if(match.blueAlliance !== undefined) {
              const alliance: string[] = alliances[match.blueAlliance];
              match.blue = alliance?.map(y => ({ team: y, status: 'waiting' })) || null;
            }
            if(match.redAlliance !== undefined) {
              const alliance: string[] = alliances[match.redAlliance];
              match.red = alliance?.map(y => ({ team: y, status: 'waiting' })) || null;
            }
          }

          return matches;
        })),
      )
    );
  }

  updateFtaNote(eventKey: string, team: string, match: string, notes: string, requestCsa = false, requestLri = false): Observable<void> {
    if(notes) {
      const requestFlagsRef = ref(this.database, `events/${eventKey}/ftaNoteRequests/${team}/${match}`);
      const requestsWrite$ = from(runTransaction(requestFlagsRef, (current: {requestCsa?: boolean; requestLri?: boolean} | null) => {
        const nextRequestCsa = !!current?.requestCsa || requestCsa;
        const nextRequestLri = !!current?.requestLri || requestLri;

        if(!nextRequestCsa && !nextRequestLri) {
          return null;
        }

        return {
          requestCsa: nextRequestCsa,
          requestLri: nextRequestLri,
        };
      }));

      return from(set(ref(this.database, `events/${eventKey}/ftaNotes/${team}/${match}`), notes)).pipe(
        switchMap(() => requestsWrite$),
        map(() => {}),
        catchError((e) => this.handleDbError(e, eventKey))
      );
    }

    return from(update(ref(this.database), {
      [`events/${eventKey}/ftaNotes/${team}/${match}`]: null,
      [`events/${eventKey}/ftaNoteRequests/${team}/${match}`]: null,
    })).pipe(
      map(() => {}),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  updateFtaGeneralNote(eventKey: string, team: string, notes: string): Observable<void> {
    return this.updateFtaNote(eventKey, team, 'general', notes);
  }

  addCustomTeamDataField(
    eventKey: string,
    name: string,
    type: 'boolean'|'string',
  ): Observable<any> {
    return from(
      push(ref(this.database, `events/${eventKey}/customTeamDataFields`), {
        name,
        created: serverTimestamp(),
        type,
      })
    ).pipe(catchError((e) => this.handleDbError(e, eventKey)));
  }

  removeCustomTeamDataField(
    eventKey: string,
    fieldKey: string,
  ): Observable<any> {
    return from(
      remove(ref(this.database, `events/${eventKey}/customTeamDataFields/${fieldKey}`))
    ).pipe(catchError((e) => this.handleDbError(e, eventKey)));
  }

  getCustomTeamDataFields(eventKey: string) {
    return objectVal<{[key: string]: {name: string, created: number, type?: 'boolean'|'string'}}>(ref(this.database, `events/${eventKey}/customTeamDataFields`)).pipe(
      map(d => d ? d : {}),
      catchError((e) => this.handleDbError(e, eventKey))
    );
  }

  getPracticeFields(eventKey: string, withEstimatedWaits = true): Observable<PracticeFields|null> {
    if(!withEstimatedWaits) {
      return objectVal<PracticeFields>(ref(this.database, `events/${eventKey}/practiceFields`));
    }

    return combineLatest([
      objectVal<PracticeFields>(ref(this.database, `events/${eventKey}/practiceFields`)),
      this.nowTimer(30000),
    ]).pipe(map(([pfs, now]) => {
      if(!pfs) return null;
      for(let pf of Object.values(pfs.fields || {})) {
        addEstimatedWaits(pf, now);
      }
      return {...pfs};
    }));
  }

  addTeamToPracticeFieldQueue(eventKey: string, fieldKey: string, team: string, priority: boolean, estimatedWaitSeconds: number|null): Observable<TransactionResult> {
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields`), (fields: {[practiceFieldKey: string]: TimedField|FmsField}) => {
      if(!fields) return {};

      if(Object.values(fields).map(f => f.queue || []).flat().find(t => t.team === team)) return;
      if(Object.values(fields).filter(f => f.type === 'TIMED').find(f => f.areas.find(a => a.team?.team === team))) return;

      const newTeam: QueuedTeam = {team, enterTimestamp: serverTimestamp() as any, priority, initialEstimatedWaitSeconds: estimatedWaitSeconds};
      if(!fields[fieldKey].queue) {
        fields[fieldKey].queue = [];
      }
      const currentQueue = fields[fieldKey].queue;
      currentQueue.push(newTeam);
      currentQueue.sort(practiceQueueComparator)
      return fields;
    })).pipe(
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  removeTeamFromPracticeFieldQueue(eventKey: string, fieldKey: string, team: string): Observable<TransactionResult> {
    let removedTeam: QueuedTeam|undefined;
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}/queue`), (currentQueue: QueuedTeam[]) => {
      if(!currentQueue) return [];

      const i = currentQueue.findIndex(t => t.team === team);
      if(i < 0) return;

      removedTeam = currentQueue[i];
      currentQueue.splice(i, 1);
      return currentQueue;
    })).pipe(
      switchMap((tr) => removedTeam ? defer(() => push(ref(this.database, `practiceFieldLog`), {...removedTeam, eventKey, fieldKey, quitTimestamp: serverTimestamp()})).pipe(map(() => tr)) : of(tr)),
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  setPracticeMatchTeam(eventKey: string, fieldKey: string, matchNumber: number, color: 'red'|'blue', stationIndex: number, team?: QueuedTeam): Observable<TransactionResult> {
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}/matches/p${matchNumber}`), (currentMatch: PracticeMatch) => {
      const newMatch: PracticeMatch = {
        matchNumber,
        red: currentMatch?.red || [{team: ''},{team: ''},{team: ''}],
        blue: currentMatch?.blue || [{team: ''},{team: ''},{team: ''}],
      };

      const existingRedIndex = newMatch.red.findIndex(t => t?.team === team?.team);
      if(existingRedIndex >= 0) {
        newMatch.red[existingRedIndex] = {team: ''};
      }
      const existingBlueIndex = newMatch.blue.findIndex(t => t?.team === team?.team);
      if(existingBlueIndex >= 0) {
        newMatch.blue[existingBlueIndex] = {team: ''};
      }

      newMatch[color][stationIndex] = team || {team: ''};
      return newMatch;
    })).pipe(
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  autoAssignPracticeMatch(eventKey: string, fieldKey: string, matchNumber: number): Observable<TransactionResult> {
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}`), (practiceField: FmsField) => {
      if(!practiceField) return {};
      if(!practiceField.queue) return;

      const currentMatch = practiceField.matches?.[`p${matchNumber}`];
      if(currentMatch?.completeTimestamp) return;

      const newMatch: PracticeMatch = {
        matchNumber,
        red: currentMatch?.red || [{team: ''},{team: ''},{team: ''}],
        blue: currentMatch?.blue || [{team: ''},{team: ''},{team: ''}],
      };

      const unassignedTeams = practiceField.queue.filter(t => !newMatch.red.concat(newMatch.blue).find(m => m?.team === t.team));
      const assignmentOrder: {color: 'red'|'blue', stationIndex: number}[] = practiceField.queue.length < 5 ?
        [{color: 'red', stationIndex: 1}, {color: 'blue', stationIndex: 1}, {color: 'red', stationIndex: 0}, {color: 'blue', stationIndex: 0}] :
        [{color: 'red', stationIndex: 0}, {color: 'red', stationIndex: 1}, {color: 'red', stationIndex: 2}, {color: 'blue', stationIndex: 0}, {color: 'blue', stationIndex: 1}, {color: 'blue', stationIndex: 2}];

      for(let assignment of assignmentOrder) {
        if(newMatch[assignment.color]?.[assignment.stationIndex]?.team) continue;
        const nextTeam = unassignedTeams.shift();
        if(!nextTeam) break;
        newMatch[assignment.color][assignment.stationIndex] = nextTeam;
      }

      if(!practiceField.matches) practiceField.matches = {};
      practiceField.matches[`p${matchNumber}`] = newMatch;

      return practiceField;
    })).pipe(
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  advancePracticeMatch(eventKey: string, fieldKey: string, matchNumber: number): Observable<TransactionResult> {
    let removedTeams: QueuedTeam[] = [];
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}`), (practiceField: FmsField) => {
      if(!practiceField) return {};
      if(!practiceField.matches?.[`p${matchNumber}`]) return;
      if(!practiceField.queue) return;

      const match = practiceField.matches[`p${matchNumber}`];
      match.completeTimestamp = serverTimestamp() as any;

      removedTeams = practiceField.queue.filter(t => match.red.concat(match.blue).find(m => m?.team === t.team))
      practiceField.queue = practiceField.queue.filter(t => !match.red.concat(match.blue).find(m => m?.team === t.team));

      return practiceField;
    })).pipe(
      switchMap((tr) => removedTeams?.length ? forkJoin(removedTeams.map(removedTeam => push(ref(this.database, `practiceFieldLog`), {...removedTeam, eventKey, fieldKey, exitTimestamp: serverTimestamp()}))).pipe(map(() => tr)) : of(tr)),
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  unadvancePracticeMatch(eventKey: string, fieldKey: string, matchNumber: number): Observable<TransactionResult> {
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}`), (practiceField: FmsField) => {
      if(!practiceField) return {};
      if(!practiceField.matches?.[`p${matchNumber}`]) return;

      const match = practiceField.matches[`p${matchNumber}`];
      match.completeTimestamp = null as any;

      const teamsToRequeue = match.red.concat(match.blue).filter((t): t is QueuedTeam => !!t?.team);
      practiceField.queue = (practiceField.queue || []).concat(teamsToRequeue);
      practiceField.queue.sort(practiceQueueComparator);

      return practiceField;
    })).pipe(
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  setPracticeAreaTeam(eventKey: string, fieldKey: string, areaIndex: number, team?: QueuedTeam, returnToQueue?: boolean): Observable<TransactionResult> {
    let removedTeam: QueuedTeam|undefined;
    return from(runTransaction(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}`), (practiceField: TimedField) => {
      if(!practiceField) return {};
      if(!practiceField.areas?.[areaIndex]) return;

      const area = practiceField.areas[areaIndex];
      const existingTeam = area.team;
      area.team = (team || null) as any;
      area.timerStarted = null as any;

      if(returnToQueue) {
        if(existingTeam) {
          if(!practiceField.queue) practiceField.queue = [];
          practiceField.queue.push(existingTeam);
          practiceField.queue.sort(practiceQueueComparator);
        }
      } else {
        if(practiceField.queue) {
          removedTeam = practiceField.queue.find(t => t.team === team?.team);
          practiceField.queue = practiceField.queue.filter(t => t.team !== team?.team);
        }
      }

      return practiceField;
    })).pipe(
      switchMap((tr) => removedTeam ? defer(() => push(ref(this.database, `practiceFieldLog`), {...removedTeam, eventKey, fieldKey, exitTimestamp: serverTimestamp()})).pipe(map(() => tr)) : of(tr)),
      catchError((e) => this.handleDbError(e, eventKey)),
    );
  }

  startPracticeTimer(eventKey: string, fieldKey: string, areaIndex: number, clear?: boolean) {
    return from(set(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}/areas/${areaIndex}/timerStarted`), clear ? null : serverTimestamp()));
  }

  setPracticeFieldOpen(eventKey: string, fieldKey: string, open: boolean) {
    return from(set(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}/open`), open));
  }

  setPracticeFieldClosedMessage(eventKey: string, fieldKey: string, closedMessage: string) {
    return from(set(ref(this.database, `events/${eventKey}/practiceFields/fields/${fieldKey}/closedMessage`), closedMessage));
  }

  setCustomTeamData(eventKey: string, team: string, fieldKey: string, value: boolean|string|null) {
    return from(set(ref(this.database, `events/${eventKey}/teams/${team}/custom/${fieldKey}`), value));
  }

  getAutoQueueKey(eventKey: string): Observable<string|null> {
    return objectVal<string|null>(ref(this.database, `events/${eventKey}/autoQueueKey`));
  }

  // Keep this in sync with fms_connector.ts
  async advancePlayoffAlliances(eventKey: string, matchAdvancingFrom: string, winningAllianceColor: 'red'|'blue') {
    const losingAllianceColor = winningAllianceColor === 'red' ? 'blue' : 'red';

    let eventComplete = false;
    let advancementSpec: MatchAdvancement|undefined;
    const transactionResult = await runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches: {[key: string]: Match}) => {
      if(!matches) return {};

      const match = matches[matchAdvancingFrom];
      advancementSpec = match.advancement;
      const winningAllianceNumber = winningAllianceColor === 'red' ? match.redAlliance : match.blueAlliance;
      const losingAllianceNumber = winningAllianceColor === 'blue' ? match.redAlliance : match.blueAlliance;
      const winningAlliance = match[winningAllianceColor];
      const losingAlliance = match[losingAllianceColor];
      const finalsType = matches['f1m1']?.finalsType || 'best2of3';

      match.winner = winningAllianceColor;

      if(!winningAlliance) {
        console.warn('Unable to advance playoffs due to missing winning alliance');
        // Abort.
        return;
      }

      function updateMatch(matchKey: string, color: 'red'|'blue', alliance: MatchTeam[], allianceNumber?: number) {
        matches[matchKey][color] = alliance.map(y => ({ team: y.team, status: 'waiting' }));
        matches[matchKey][`${color}Alliance`] = allianceNumber;
        delete matches[matchKey][`${color}LineupSet`];
      }

      if(advancementSpec) {
        if(advancementSpec.winner.match === 'f1') {
          updateMatch('f1m1', advancementSpec.winner.color, winningAlliance, winningAllianceNumber);

          if(matches['f1m2']) {
            updateMatch('f1m2', advancementSpec.winner.color, winningAlliance, winningAllianceNumber);
          }
          if(matches['f1m3']) {
            updateMatch('f1m3', advancementSpec.winner.color, winningAlliance, winningAllianceNumber);
          }
        } else {
          updateMatch(advancementSpec.winner.match, advancementSpec.winner.color, winningAlliance, winningAllianceNumber);
        }

        if(advancementSpec.loser && losingAlliance) {
          updateMatch(advancementSpec.loser.match, advancementSpec.loser.color, losingAlliance, losingAllianceNumber);
        }
      }

      if(finalsType === 'red1blue2' && matchAdvancingFrom === 'f1m1') {
        if(winningAllianceColor === 'red') {
          delete matches['f1m2'];
          eventComplete = true;
        } else {
          matches['f1m2'] = {
            blue: match.blue?.map(t => ({...t, status: 'waiting'})),
            red: match.red?.map(t => ({...t, status: 'waiting'})),
            blueAlliance: match.blueAlliance,
            redAlliance: match.redAlliance,
            status: 'waiting',
            uncertain: false,
          }
        }
      }

      if(finalsType === 'best2of3' && matchAdvancingFrom === 'f1m2') {
        const winnerOfMatch1 = matches['f1m1'].winner;

        if(winningAllianceColor === winnerOfMatch1) {
          delete matches['f1m3'];
          eventComplete = true;
        } else {
          matches['f1m3'] = {
            blue: match.blue?.map(t => ({...t, status: 'waiting'})),
            red: match.red?.map(t => ({...t, status: 'waiting'})),
            blueAlliance: match.blueAlliance,
            redAlliance: match.redAlliance,
            status: 'waiting',
            uncertain: false,
            location: (matches['f1m3']?.location || null) as string|undefined,
            timeInterval: (matches['f1m3']?.timeInterval || null) as number|undefined,
          }
        }
      }

      if(finalsType === 'best2of3' && matchAdvancingFrom === 'f1m3') {
        eventComplete = true;
      }

      return matches;
    });

    if(!transactionResult.committed) {
      throw new Error('Unable to advance playoffs');
    }

    // Cleanup any potential out-of-date lineups.
    if(advancementSpec) {
      await remove(ref(this.database, `events/${eventKey}/lineups/${advancementSpec.winner.match}`));
      if(advancementSpec?.loser) {
        await remove(ref(this.database, `events/${eventKey}/lineups/${advancementSpec.loser.match}`));
      }
    }

    return {eventComplete};
  }

  async undoPlayoffAdvancement(eventKey: string, matchAdvancingFrom: string) {
    let advancementSpec: MatchAdvancement|undefined;
    const transactionResult = await runTransaction(ref(this.database, `events/${eventKey}/matches`), (matches: {[key: string]: Match}) => {
      if(!matches) return {};

      const match = matches[matchAdvancingFrom];
      advancementSpec = match.advancement;

      delete match.winner;

      function clearMatchAlliance(matchKey: string, color: 'red'|'blue') {
        delete matches[matchKey][color];
        delete matches[matchKey][`${color}Alliance`];
        delete matches[matchKey][`${color}LineupSet`];
      }

      if(advancementSpec) {
        if(advancementSpec.winner.match === 'f1') {
          clearMatchAlliance('f1m1', advancementSpec.winner.color);

          if(matches['f1m2']) {
            clearMatchAlliance('f1m2', advancementSpec.winner.color);
          }
          if(matches['f1m3']) {
            clearMatchAlliance('f1m3', advancementSpec.winner.color);
          }
        } else {
          clearMatchAlliance(advancementSpec.winner.match, advancementSpec.winner.color);
        }

        if(advancementSpec.loser) {
          clearMatchAlliance(advancementSpec.loser.match, advancementSpec.loser.color);
        }
      }

      return matches;
    });

    if(!transactionResult.committed) {
      throw new Error('Unable to undo playoff advancement');
    }

    // Cleanup any potential out-of-date lineups.
    if(advancementSpec) {
      await remove(ref(this.database, `events/${eventKey}/lineups/${advancementSpec.winner.match}`));
      if(advancementSpec?.loser) {
        await remove(ref(this.database, `events/${eventKey}/lineups/${advancementSpec.loser.match}`));
      }
    }

    return;
  }

  private handleDbError(error: { code?: string }, eventKey?: string) {
    if (error.code === 'PERMISSION_DENIED') {
      if (eventKey) {
        this.router.navigateByUrl(`/event/${eventKey}`);
        this.toast.show('You no longer have access to this event', {id: 'permission-denied'});
      } else {
        this.router.navigateByUrl(`/`);
        this.toast.show('You no longer have access to this page', {id: 'permission-denied'});
      }
      if(this.analytics) {
        logEvent(this.analytics, 'permission-denied', { eventKey: eventKey });
      }

      return EMPTY;
    }

    console.warn('Unknown db error', error.code, error);

    throw error;
  }
}
